import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Flag as FlagIcon,
  Schedule as ScheduleIcon,
  ArrowUpward as PromoteIcon,
  RateReview as RequestIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  PlaylistAddCheck as ImplementIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';
import { 
  ComplianceTag, 
  ComplianceTagDetailResponse, 
  ReviewSubmission,
  ThresholdReviewRequest,
  ThresholdRequestResolution,
  CreateTagRequest,
} from '../types';

// Status badge configuration
const STATUS_CONFIG = {
  pending: { color: 'default' as const, icon: <ScheduleIcon fontSize="small" />, label: 'Pending' },
  reviewed: { color: 'success' as const, icon: <CheckCircleIcon fontSize="small" />, label: 'Reviewed' },
  needs_revision: { color: 'warning' as const, icon: <WarningIcon fontSize="small" />, label: 'Needs Revision' },
  flagged: { color: 'error' as const, icon: <FlagIcon fontSize="small" />, label: 'Flagged' },
};

// Category badge colors
const CATEGORY_COLORS = {
  diet: 'info' as const,
  allergen: 'warning' as const,
  ailment: 'secondary' as const,
  custom: 'default' as const,
};

// Status badge configuration for threshold requests
const REQUEST_STATUS_CONFIG = {
  pending: { color: 'default' as const, icon: <ScheduleIcon fontSize="small" />, label: 'Pending' },
  approved: { color: 'success' as const, icon: <ApproveIcon fontSize="small" />, label: 'Approved' },
  rejected: { color: 'error' as const, icon: <RejectIcon fontSize="small" />, label: 'Rejected' },
  deferred: { color: 'warning' as const, icon: <ScheduleIcon fontSize="small" />, label: 'Deferred' },
  implemented: { color: 'info' as const, icon: <ImplementIcon fontSize="small" />, label: 'Implemented' },
};

export function ComplianceReviewTab() {
  const queryClient = useQueryClient();
  
  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<number>(0);
  
  // Filter state for NCM tags
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [systemFilter, setSystemFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Filter state for threshold requests
  const [requestStatusFilter, setRequestStatusFilter] = useState<string>('pending');
  
  // Modal state for NCM tags
  const [selectedTag, setSelectedTag] = useState<ComplianceTag | null>(null);
  const [tagDetail, setTagDetail] = useState<ComplianceTagDetailResponse | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'reviewed' | 'needs_revision' | 'flagged'>('reviewed');
  const [reviewNotes, setReviewNotes] = useState<string>('');
  const [editedThreshold, setEditedThreshold] = useState<string>('');
  const [editedKeywords, setEditedKeywords] = useState<string>('');
  const [editedNutrient, setEditedNutrient] = useState<string>('');
  const [editedComparison, setEditedComparison] = useState<string>('');
  const [editedUnit, setEditedUnit] = useState<string>('');
  const [editedAlternateNames, setEditedAlternateNames] = useState<string>('');
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  
  // Create Tag Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagId, setNewTagId] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newCategory, setNewCategory] = useState<'diet' | 'allergen' | 'ailment' | 'custom'>('diet');
  const [newEvalType, setNewEvalType] = useState<'nutrition' | 'ingredient' | 'both' | 'ai_only'>('ingredient');
  const [newNutrient, setNewNutrient] = useState('');
  const [newComparison, setNewComparison] = useState('');
  const [newThreshold, setNewThreshold] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [newAlternateNames, setNewAlternateNames] = useState('');
  const [newSource, setNewSource] = useState('');
  
  // Modal state for threshold requests
  const [selectedRequest, setSelectedRequest] = useState<ThresholdReviewRequest | null>(null);
  const [requestAction, setRequestAction] = useState<'approve' | 'reject' | 'defer' | 'implement'>('approve');
  const [requestReviewNotes, setRequestReviewNotes] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [applyChanges, setApplyChanges] = useState<boolean>(true);

  // Fetch compliance tags
  const { data, isLoading, error } = useQuery({
    queryKey: ['complianceTags', statusFilter, categoryFilter, systemFilter, searchQuery],
    queryFn: () => adminApi.getComplianceTags({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
      is_system: systemFilter !== 'all' ? systemFilter : undefined,
      search: searchQuery || undefined,
    }),
    refetchInterval: 60000, // Refresh every minute
  });
  
  // Fetch threshold review requests
  const { 
    data: requestsData, 
    isLoading: isLoadingRequests, 
    error: requestsError 
  } = useQuery({
    queryKey: ['thresholdRequests', requestStatusFilter],
    queryFn: () => adminApi.getThresholdReviewRequests({
      status: requestStatusFilter !== 'all' ? requestStatusFilter : undefined,
    }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (params: { ncmTagId: string; updates: ReviewSubmission }) =>
      adminApi.updateComplianceTag(params.ncmTagId, params.updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTags'] });
      handleCloseModal();
    },
  });

  // Promote mutation
  const promoteMutation = useMutation({
    mutationFn: (params: { ncmTagId: string; notes: string }) =>
      adminApi.promoteComplianceTag(params.ncmTagId, params.notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTags'] });
      handleCloseModal();
    },
  });
  
  // Resolve threshold request mutation
  const resolveRequestMutation = useMutation({
    mutationFn: (params: { requestId: string; resolution: ThresholdRequestResolution }) =>
      adminApi.resolveThresholdRequest(params.requestId, params.resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thresholdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['complianceTags'] });
      handleCloseRequestModal();
    },
  });

  // Create tag mutation
  const createMutation = useMutation({
    mutationFn: (tagData: CreateTagRequest) => adminApi.createComplianceTag(tagData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTags'] });
      handleCloseCreateModal();
    },
  });

  // Delete tag mutation
  const deleteMutation = useMutation({
    mutationFn: (ncmTagId: string) => adminApi.deleteComplianceTag(ncmTagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceTags'] });
      handleCloseModal();
    },
  });

  const handleOpenReview = async (tag: ComplianceTag) => {
    setSelectedTag(tag);
    setReviewDecision('reviewed');
    setReviewNotes(tag.review_notes || '');
    setEditedThreshold(tag.nutrition_threshold?.toString() || '');
    setEditedKeywords(tag.ingredient_keywords || '');
    setEditedNutrient(tag.nutrition_nutrient || '');
    setEditedComparison(tag.nutrition_comparison || '');
    setEditedUnit(tag.nutrition_unit || '');
    setEditedAlternateNames(tag.alternate_names || '');
    
    // Fetch detailed info
    setIsLoadingDetail(true);
    try {
      const detail = await adminApi.getComplianceTagDetail(tag.ncm_tag_id);
      setTagDetail(detail);
    } catch (err) {
      console.error('Failed to load tag detail:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedTag(null);
    setTagDetail(null);
    setReviewNotes('');
    setEditedThreshold('');
    setEditedKeywords('');
    setEditedNutrient('');
    setEditedComparison('');
    setEditedUnit('');
    setEditedAlternateNames('');
  };
  
  const handleOpenRequestModal = (request: ThresholdReviewRequest) => {
    setSelectedRequest(request);
    setRequestAction('approve');
    setRequestReviewNotes('');
    setRejectionReason('');
    setApplyChanges(true);
  };
  
  const handleCloseRequestModal = () => {
    setSelectedRequest(null);
    setRequestReviewNotes('');
    setRejectionReason('');
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewTagId('');
    setNewDisplayName('');
    setNewCategory('diet');
    setNewEvalType('ingredient');
    setNewNutrient('');
    setNewComparison('');
    setNewThreshold('');
    setNewUnit('');
    setNewKeywords('');
    setNewAlternateNames('');
    setNewSource('');
  };

  const handleCreateTag = () => {
    const tagData: CreateTagRequest = {
      ncm_tag_id: newTagId,
      display_name: newDisplayName,
      tag_category: newCategory,
      evaluation_type: newEvalType,
      nutrition_nutrient: newNutrient || undefined,
      nutrition_threshold: newThreshold ? parseFloat(newThreshold) : undefined,
      nutrition_unit: newUnit || undefined,
      nutrition_comparison: newComparison || undefined,
      ingredient_keywords: newKeywords || undefined,
      alternate_names: newAlternateNames || undefined,
      ncm_source: newSource || undefined,
    };
    createMutation.mutate(tagData);
  };

  const handleDeleteTag = () => {
    if (!selectedTag) return;
    if (window.confirm(`Are you sure you want to delete "${selectedTag.display_name}"? This will also invalidate all cached compliance results for this tag.`)) {
      deleteMutation.mutate(selectedTag.ncm_tag_id);
    }
  };
  
  const handleSubmitRequestResolution = () => {
    if (!selectedRequest) return;
    
    const resolution: ThresholdRequestResolution = {
      action: requestAction,
      review_notes: requestReviewNotes,
      reviewer_id: 'head-dietitian', // This would come from auth context in real implementation
      reviewer_name: 'Head Dietitian',
      apply_changes: requestAction === 'approve' ? applyChanges : undefined,
    };
    
    if (requestAction === 'reject') {
      resolution.rejection_reason = rejectionReason;
    }
    
    resolveRequestMutation.mutate({ requestId: selectedRequest.id, resolution });
  };

  const handleSubmitReview = () => {
    if (!selectedTag) return;
    
    const updates: ReviewSubmission = {
      review_status: reviewDecision,
      review_notes: reviewNotes,
    };

    // Always include fields if they were changed (regardless of review decision)
    if (editedThreshold && editedThreshold !== selectedTag.nutrition_threshold?.toString()) {
      updates.nutrition_threshold = parseFloat(editedThreshold);
    }
    if (editedKeywords !== selectedTag.ingredient_keywords) {
      updates.ingredient_keywords = editedKeywords;
    }
    if (editedNutrient !== selectedTag.nutrition_nutrient) {
      updates.nutrition_nutrient = editedNutrient || undefined;
    }
    if (editedComparison !== selectedTag.nutrition_comparison) {
      updates.nutrition_comparison = editedComparison || undefined;
    }
    if (editedUnit !== selectedTag.nutrition_unit) {
      updates.nutrition_unit = editedUnit || undefined;
    }
    if (editedAlternateNames !== selectedTag.alternate_names) {
      updates.alternate_names = editedAlternateNames || undefined;
    }

    updateMutation.mutate({ ncmTagId: selectedTag.ncm_tag_id, updates });
  };

  const handlePromote = () => {
    if (!selectedTag) return;
    promoteMutation.mutate({ ncmTagId: selectedTag.ncm_tag_id, notes: reviewNotes });
  };

  const formatRule = (tag: ComplianceTag): string => {
    const parts: string[] = [];
    
    // Nutrition threshold
    if (tag.evaluation_type === 'nutrition' || tag.evaluation_type === 'both') {
      if (tag.nutrition_nutrient && tag.nutrition_threshold !== undefined) {
        const comparison = tag.nutrition_comparison === 'less_than' ? '<' : 
                          tag.nutrition_comparison === 'greater_than' ? '>' : 'between';
        parts.push(`${tag.nutrition_nutrient} ${comparison} ${tag.nutrition_threshold}${tag.nutrition_unit || ''}${tag.nutrition_per_serving ? '/serving' : ''}`);
      }
    }
    
    // Ingredient keywords
    if (tag.evaluation_type === 'ingredient' || tag.evaluation_type === 'both') {
      if (tag.ingredient_keywords) {
        const keywordList = tag.ingredient_keywords.split(',');
        const displayKeywords = keywordList.slice(0, 3).join(', ');
        const moreCount = keywordList.length > 3 ? ` (+${keywordList.length - 3} more)` : '';
        parts.push(`Avoid: ${displayKeywords}${moreCount}`);
      }
    }
    
    if (parts.length === 0) {
      return tag.evaluation_type === 'ai_only' ? 'AI Analysis Required' : 'Complex rule';
    }
    
    // Join with " + " for tags that have both conditions
    return parts.join(' + ');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load compliance tags: {String(error)}</Alert>;
  }

  const { tags, summary } = data || { tags: [], summary: { total: 0, pending: 0, reviewed: 0, needs_revision: 0, flagged: 0 } };
  const { requests, summary: requestsSummary } = requestsData || { 
    requests: [], 
    summary: { total_requests: 0, pending_count: 0, approved_count: 0, rejected_count: 0, implemented_count: 0 } 
  };
  
  const pendingRequestsCount = requestsSummary?.pending_count || 0;

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Compliance Review
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review NCM dietary compliance thresholds and dietitian threshold change requests
      </Typography>
      
      {/* Sub-tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeSubTab} 
          onChange={(_, newValue) => setActiveSubTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab 
            label="🏷️ NCM Tags" 
            icon={<Chip size="small" label={summary.total} sx={{ ml: 1 }} />}
            iconPosition="end"
          />
          <Tab 
            label="📋 Threshold Requests" 
            icon={
              <Badge badgeContent={pendingRequestsCount} color="error" sx={{ ml: 1 }}>
                <RequestIcon fontSize="small" />
              </Badge>
            }
            iconPosition="end"
          />
        </Tabs>
      </Paper>
      
      {/* Tab Content */}
      {activeSubTab === 0 && (
        <>
          {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
            <Typography variant="h4">{summary.total}</Typography>
            <Typography variant="body2" color="text.secondary">Total Tags</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200' }}>
            <Typography variant="h4">{summary.pending}</Typography>
            <Typography variant="body2" color="text.secondary">⏳ Pending</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
            <Typography variant="h4" color="success.dark">{summary.reviewed}</Typography>
            <Typography variant="body2" color="success.dark">✅ Reviewed</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
            <Typography variant="h4" color="warning.dark">{summary.needs_revision}</Typography>
            <Typography variant="body2" color="warning.dark">✏️ Needs Revision</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
            <Typography variant="h4" color="error.dark">{summary.flagged}</Typography>
            <Typography variant="body2" color="error.dark">🚩 Flagged</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="reviewed">Reviewed</MenuItem>
              <MenuItem value="needs_revision">Needs Revision</MenuItem>
              <MenuItem value="flagged">Flagged</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="diet">Diet</MenuItem>
              <MenuItem value="allergen">Allergen</MenuItem>
              <MenuItem value="ailment">Ailment</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Tag Type"
              value={systemFilter}
              onChange={(e) => setSystemFilter(e.target.value)}
            >
              <MenuItem value="all">All Tags</MenuItem>
              <MenuItem value="true">System Tags</MenuItem>
              <MenuItem value="false">Custom Tags</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => setShowCreateModal(true)}
              startIcon={<AddIcon />}
            >
              Add Tag
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tag List */}
      <Grid container spacing={2}>
        {tags.length === 0 ? (
          <Grid size={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No compliance tags found matching your filters
              </Typography>
            </Paper>
          </Grid>
        ) : (
          tags.map((tag) => (
            <Grid size={{ xs: 12, md: 6 }} key={tag.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      <Typography variant="h6" component="div">
                        🏷️ {tag.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tag.ncm_tag_id}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      icon={STATUS_CONFIG[tag.review_status || 'pending']?.icon}
                      label={STATUS_CONFIG[tag.review_status || 'pending']?.label}
                      color={STATUS_CONFIG[tag.review_status || 'pending']?.color}
                    />
                  </Box>

                  <Box display="flex" gap={1} mb={2}>
                    <Chip
                      size="small"
                      label={tag.tag_category}
                      color={CATEGORY_COLORS[tag.tag_category]}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={tag.evaluation_type}
                      variant="outlined"
                    />
                    {!tag.is_system_tag && (
                      <Chip size="small" label="Custom" color="default" />
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    {formatRule(tag)}
                  </Typography>

                  {tag.ncm_source && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                      Source: {tag.ncm_source}
                    </Typography>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenReview(tag)}
                    >
                      Review
                    </Button>
                    {tag.reviewed_at && (
                      <Typography variant="caption" color="text.secondary">
                        Last reviewed: {new Date(tag.reviewed_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

        </>
      )}
      
      {/* Threshold Requests Tab */}
      {activeSubTab === 1 && (
        <>
          {/* Requests Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200' }}>
                <Typography variant="h4">{requestsSummary?.pending_count || 0}</Typography>
                <Typography variant="body2" color="text.secondary">⏳ Pending</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                <Typography variant="h4" color="success.dark">{requestsSummary?.approved_count || 0}</Typography>
                <Typography variant="body2" color="success.dark">✅ Approved</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                <Typography variant="h4" color="error.dark">{requestsSummary?.rejected_count || 0}</Typography>
                <Typography variant="body2" color="error.dark">❌ Rejected</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 'grow' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h4" color="info.dark">{requestsSummary?.implemented_count || 0}</Typography>
                <Typography variant="body2" color="info.dark">🚀 Implemented</Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Requests Filter */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <TextField
              select
              size="small"
              label="Status"
              value={requestStatusFilter}
              onChange={(e) => setRequestStatusFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="deferred">Deferred</MenuItem>
              <MenuItem value="implemented">Implemented</MenuItem>
            </TextField>
          </Paper>
          
          {/* Requests List */}
          {isLoadingRequests ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : requestsError ? (
            <Alert severity="error">Failed to load threshold requests</Alert>
          ) : requests.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No threshold review requests found
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {requests.map((request) => (
                <Grid size={{ xs: 12, md: 6 }} key={request.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box>
                          <Typography variant="h6">
                            📝 {request.ncm_mapping?.display_name || request.ncm_tag_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Requested by: {request.requested_by_name || 'Unknown'}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          icon={REQUEST_STATUS_CONFIG[request.request_status]?.icon}
                          label={REQUEST_STATUS_CONFIG[request.request_status]?.label}
                          color={REQUEST_STATUS_CONFIG[request.request_status]?.color}
                        />
                      </Box>
                      
                      <Box display="flex" gap={1} mb={2}>
                        <Chip
                          size="small"
                          label={request.ncm_mapping?.tag_category || 'unknown'}
                          variant="outlined"
                        />
                      </Box>
                      
                      {/* Proposed Change */}
                      {request.proposed_threshold !== null && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            PROPOSED CHANGE
                          </Typography>
                          <Typography variant="body2">
                            {request.current_threshold}{request.current_unit} → <strong>{request.proposed_threshold}{request.proposed_unit || request.current_unit}</strong>
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Reasoning Preview */}
                      <Typography variant="body2" sx={{ mb: 2 }} noWrap>
                        💬 {request.reasoning}
                      </Typography>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenRequestModal(request)}
                        >
                          Review Request
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(request.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* NCM Tag Review Modal */}
      <Dialog
        open={!!selectedTag}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review Tag: {selectedTag?.display_name}
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingDetail ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Current Rule Section */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  CURRENT RULE
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Tag ID</Typography>
                      <Typography variant="body2">{selectedTag?.ncm_tag_id}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Category</Typography>
                      <Typography variant="body2">{selectedTag?.tag_category}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Evaluation Type</Typography>
                      <Typography variant="body2">{selectedTag?.evaluation_type}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">System Tag</Typography>
                      <Typography variant="body2">{selectedTag?.is_system_tag ? 'Yes' : 'No (Custom)'}</Typography>
                    </Grid>
                  </Grid>

                  {(selectedTag?.evaluation_type === 'nutrition' || selectedTag?.evaluation_type === 'both') && (
                    <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="subtitle2" gutterBottom>Threshold Rule</Typography>
                      <Grid container spacing={2}>
                        <Grid size={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Nutrient"
                            value={editedNutrient}
                            onChange={(e) => setEditedNutrient(e.target.value)}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="calories">Calories</MenuItem>
                            <MenuItem value="fat">Fat</MenuItem>
                            <MenuItem value="saturated_fat">Saturated Fat</MenuItem>
                            <MenuItem value="carbs">Carbohydrates</MenuItem>
                            <MenuItem value="sugar">Sugar</MenuItem>
                            <MenuItem value="fiber">Fiber</MenuItem>
                            <MenuItem value="protein">Protein</MenuItem>
                            <MenuItem value="sodium">Sodium</MenuItem>
                            <MenuItem value="cholesterol">Cholesterol</MenuItem>
                            <MenuItem value="potassium">Potassium</MenuItem>
                            <MenuItem value="calcium">Calcium</MenuItem>
                            <MenuItem value="iron">Iron</MenuItem>
                            <MenuItem value="caffeine">Caffeine</MenuItem>
                            <MenuItem value="omega-3">Omega-3</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid size={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Comparison"
                            value={editedComparison}
                            onChange={(e) => setEditedComparison(e.target.value)}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="less_than">Less than (&lt;)</MenuItem>
                            <MenuItem value="greater_than">Greater than (&gt;)</MenuItem>
                            <MenuItem value="between">Between</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid size={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Threshold"
                            type="number"
                            value={editedThreshold}
                            onChange={(e) => setEditedThreshold(e.target.value)}
                            helperText="Value to compare against"
                          />
                        </Grid>
                        <Grid size={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Unit"
                            value={editedUnit}
                            onChange={(e) => setEditedUnit(e.target.value)}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="g">Grams (g)</MenuItem>
                            <MenuItem value="mg">Milligrams (mg)</MenuItem>
                            <MenuItem value="mcg">Micrograms (mcg)</MenuItem>
                            <MenuItem value="kcal">Calories (kcal)</MenuItem>
                            <MenuItem value="%">Percent (%)</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {(selectedTag?.evaluation_type === 'ingredient' || selectedTag?.evaluation_type === 'both') && (
                    <Box mt={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Ingredient Keywords"
                        value={editedKeywords}
                        onChange={(e) => setEditedKeywords(e.target.value)}
                        multiline
                        rows={3}
                        helperText="Comma-separated keywords to check in ingredients. Editing will invalidate cached results."
                      />
                    </Box>
                  )}

                  {/* Alternate Names */}
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Alternate Names / Aliases"
                      value={editedAlternateNames}
                      onChange={(e) => setEditedAlternateNames(e.target.value)}
                      multiline
                      rows={2}
                      helperText="Comma-separated alternative names for matching (e.g., acid reflux, gerd, heartburn)"
                    />
                  </Box>
                </Paper>
              </Box>

              {/* Source Documentation */}
              {selectedTag?.ncm_source && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    SOURCE DOCUMENTATION
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2">{selectedTag.ncm_source}</Typography>
                  </Paper>
                </Box>
              )}

              {/* Usage Statistics */}
              {tagDetail && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    USAGE STATISTICS
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={4}>
                        <Typography variant="h5" align="center">{tagDetail.usage_stats.recipes_checked}</Typography>
                        <Typography variant="caption" align="center" display="block" color="text.secondary">
                          Recipes Checked
                        </Typography>
                      </Grid>
                      <Grid size={4}>
                        <Typography variant="h5" align="center" color="success.main">
                          {tagDetail.usage_stats.compliant_count}
                        </Typography>
                        <Typography variant="caption" align="center" display="block" color="text.secondary">
                          Compliant
                        </Typography>
                      </Grid>
                      <Grid size={4}>
                        <Typography variant="h5" align="center" color="error.main">
                          {tagDetail.usage_stats.non_compliant_count}
                        </Typography>
                        <Typography variant="caption" align="center" display="block" color="text.secondary">
                          Non-Compliant
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}

              <Divider />

              {/* Review Decision */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  YOUR REVIEW
                </Typography>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Decision</FormLabel>
                  <RadioGroup
                    value={reviewDecision}
                    onChange={(e) => setReviewDecision(e.target.value as typeof reviewDecision)}
                  >
                    <FormControlLabel
                      value="reviewed"
                      control={<Radio color="success" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <span>Approve - Rule is clinically accurate</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="needs_revision"
                      control={<Radio color="warning" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <WarningIcon color="warning" fontSize="small" />
                          <span>Needs Revision - Threshold should be adjusted</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="flagged"
                      control={<Radio color="error" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <FlagIcon color="error" fontSize="small" />
                          <span>Flag for Discussion - Needs team review</span>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Review Notes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add your clinical assessment notes..."
                  sx={{ mt: 2 }}
                  required
                />

                {(editedThreshold !== selectedTag?.nutrition_threshold?.toString() || 
                  editedKeywords !== selectedTag?.ingredient_keywords ||
                  editedNutrient !== selectedTag?.nutrition_nutrient ||
                  editedComparison !== selectedTag?.nutrition_comparison ||
                  editedUnit !== selectedTag?.nutrition_unit) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    ⚠️ You've modified rule values. Saving will invalidate all cached compliance results for this tag and recipes will be re-evaluated on next view.
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {selectedTag && !selectedTag.is_system_tag && selectedTag.review_status === 'reviewed' && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PromoteIcon />}
              onClick={handlePromote}
              disabled={promoteMutation.isPending}
            >
              {promoteMutation.isPending ? 'Promoting...' : 'Promote to System Tag'}
            </Button>
          )}
          {selectedTag && !selectedTag.is_system_tag && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteTag}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Tag'}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseModal} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={updateMutation.isPending || !reviewNotes.trim()}
          >
            {updateMutation.isPending ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Threshold Request Resolution Modal */}
      <Dialog
        open={!!selectedRequest}
        onClose={handleCloseRequestModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review Threshold Request: {selectedRequest?.ncm_mapping?.display_name || selectedRequest?.ncm_tag_id}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Requester Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  REQUESTED BY
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1">{selectedRequest.requested_by_name || 'Unknown Dietitian'}</Typography>
                  {selectedRequest.requested_by_email && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedRequest.requested_by_email}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Submitted: {new Date(selectedRequest.created_at).toLocaleString()}
                  </Typography>
                </Paper>
              </Box>
              
              {/* Current vs Proposed */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  PROPOSED CHANGE
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Current Threshold</Typography>
                      <Typography variant="h6">
                        {selectedRequest.current_threshold !== null 
                          ? `${selectedRequest.current_threshold}${selectedRequest.current_unit || ''}`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Proposed Threshold</Typography>
                      <Typography variant="h6" color="warning.main">
                        {selectedRequest.proposed_threshold !== null
                          ? `${selectedRequest.proposed_threshold}${selectedRequest.proposed_unit || selectedRequest.current_unit || ''}`
                          : 'No change proposed'}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {selectedRequest.proposed_keywords && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary">Proposed Keywords</Typography>
                      <Typography variant="body2">{selectedRequest.proposed_keywords}</Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
              
              {/* Reasoning */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  EVIDENCE-BASED REASONING
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1">{selectedRequest.reasoning}</Typography>
                </Paper>
              </Box>
              
              {/* Supporting Evidence */}
              {selectedRequest.supporting_evidence && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    SUPPORTING EVIDENCE
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2">{selectedRequest.supporting_evidence}</Typography>
                  </Paper>
                </Box>
              )}
              
              {/* Patient Context */}
              {selectedRequest.patient_context && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    PATIENT CONTEXT
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2">{selectedRequest.patient_context}</Typography>
                  </Paper>
                </Box>
              )}
              
              <Divider />
              
              {/* Resolution Decision */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  YOUR DECISION
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={requestAction}
                    onChange={(e) => setRequestAction(e.target.value as typeof requestAction)}
                  >
                    <FormControlLabel
                      value="approve"
                      control={<Radio color="success" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <ApproveIcon color="success" fontSize="small" />
                          <span>Approve - Request is valid and evidence-based</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="implement"
                      control={<Radio color="info" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <ImplementIcon color="info" fontSize="small" />
                          <span>Approve & Implement - Apply changes to NCM threshold now</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="defer"
                      control={<Radio color="warning" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <ScheduleIcon color="warning" fontSize="small" />
                          <span>Defer - Needs more discussion or research</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="reject"
                      control={<Radio color="error" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <RejectIcon color="error" fontSize="small" />
                          <span>Reject - Request is not clinically sound</span>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Review Notes"
                  value={requestReviewNotes}
                  onChange={(e) => setRequestReviewNotes(e.target.value)}
                  placeholder="Add your clinical assessment..."
                  sx={{ mt: 2 }}
                />
                
                {requestAction === 'reject' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Rejection Reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explain why this request is being rejected..."
                    sx={{ mt: 2 }}
                    required
                    error={requestAction === 'reject' && !rejectionReason}
                    helperText={requestAction === 'reject' && !rejectionReason ? 'Required when rejecting' : ''}
                  />
                )}
                
                {requestAction === 'approve' && (
                  <FormControlLabel
                    control={
                      <Radio
                        checked={applyChanges}
                        onChange={(e) => setApplyChanges(e.target.checked)}
                        color="info"
                      />
                    }
                    label="Also apply changes to NCM threshold"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseRequestModal} disabled={resolveRequestMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={requestAction === 'reject' ? 'error' : requestAction === 'implement' ? 'info' : 'primary'}
            onClick={handleSubmitRequestResolution}
            disabled={
              resolveRequestMutation.isPending || 
              (requestAction === 'reject' && !rejectionReason)
            }
          >
            {resolveRequestMutation.isPending 
              ? 'Processing...' 
              : requestAction === 'reject' 
                ? 'Reject Request'
                : requestAction === 'implement'
                  ? 'Approve & Implement'
                  : requestAction === 'defer'
                    ? 'Defer Request'
                    : 'Approve Request'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Tag Modal */}
      <Dialog
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Compliance Tag</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Tag ID"
                  value={newTagId}
                  onChange={(e) => setNewTagId(e.target.value)}
                  helperText="Unique identifier (lowercase, hyphens allowed)"
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  helperText="Human-readable name"
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as any)}
                  required
                >
                  <MenuItem value="diet">Diet</MenuItem>
                  <MenuItem value="allergen">Allergen</MenuItem>
                  <MenuItem value="ailment">Ailment</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  select
                  fullWidth
                  label="Evaluation Type"
                  value={newEvalType}
                  onChange={(e) => setNewEvalType(e.target.value as any)}
                  required
                >
                  <MenuItem value="nutrition">Nutrition (threshold-based)</MenuItem>
                  <MenuItem value="ingredient">Ingredient (keyword-based)</MenuItem>
                  <MenuItem value="both">Both (nutrition + ingredient)</MenuItem>
                  <MenuItem value="ai_only">AI Only (no rules)</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {(newEvalType === 'nutrition' || newEvalType === 'both') && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>Nutrition Threshold</Typography>
                <Grid container spacing={2}>
                  <Grid size={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Nutrient"
                      value={newNutrient}
                      onChange={(e) => setNewNutrient(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="calories">Calories</MenuItem>
                      <MenuItem value="fat">Fat</MenuItem>
                      <MenuItem value="saturated_fat">Saturated Fat</MenuItem>
                      <MenuItem value="carbs">Carbohydrates</MenuItem>
                      <MenuItem value="sugar">Sugar</MenuItem>
                      <MenuItem value="fiber">Fiber</MenuItem>
                      <MenuItem value="protein">Protein</MenuItem>
                      <MenuItem value="sodium">Sodium</MenuItem>
                      <MenuItem value="cholesterol">Cholesterol</MenuItem>
                      <MenuItem value="potassium">Potassium</MenuItem>
                      <MenuItem value="caffeine">Caffeine</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Comparison"
                      value={newComparison}
                      onChange={(e) => setNewComparison(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="less_than">Less than (&lt;)</MenuItem>
                      <MenuItem value="greater_than">Greater than (&gt;)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Threshold"
                      type="number"
                      value={newThreshold}
                      onChange={(e) => setNewThreshold(e.target.value)}
                    />
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Unit"
                      value={newUnit}
                      onChange={(e) => setNewUnit(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="g">Grams (g)</MenuItem>
                      <MenuItem value="mg">Milligrams (mg)</MenuItem>
                      <MenuItem value="mcg">Micrograms (mcg)</MenuItem>
                      <MenuItem value="kcal">Calories (kcal)</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {(newEvalType === 'ingredient' || newEvalType === 'both') && (
              <TextField
                fullWidth
                label="Ingredient Keywords"
                value={newKeywords}
                onChange={(e) => setNewKeywords(e.target.value)}
                multiline
                rows={2}
                helperText="Comma-separated keywords to check in ingredients"
              />
            )}

            <TextField
              fullWidth
              label="Alternate Names / Aliases"
              value={newAlternateNames}
              onChange={(e) => setNewAlternateNames(e.target.value)}
              multiline
              rows={2}
              helperText="Comma-separated alternative names for matching (e.g., acid reflux, gerd, heartburn)"
            />

            <TextField
              fullWidth
              label="NCM Source / Documentation"
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              helperText="Reference source for thresholds (e.g., FDA, NCM guidelines)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCreateModal} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreateTag}
            disabled={createMutation.isPending || !newTagId.trim() || !newDisplayName.trim()}
            startIcon={<AddIcon />}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Tag'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

